import React, { useContext, useEffect, useState } from 'react'
import { PlpProduct, SearchResponse, WidgetCategoryLink, WidgetRegionLink } from '@/types'
import Head from 'next/head'
import { getIndexTag } from '@/helpers/SeoHelper'
import { useRouter } from 'next/router'
import { formatSearchTitle } from '@/helpers/SearchHelper'
import { PageContent, HeadContainer, NoResultsWrapper, ProductListContainer } from './SearchTemplatePage.styles'
import Container from './Container'
import Link from 'next/link'
import { Body, Heading, SearchResult, Spacing } from '@big-red-group/storefront-common-ui'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import FiltersListingPreview from './widgets/FiltersListingPreview'
import Breadcrumb from './Breadcrumb'
import { getUrlPrefix } from '@/helpers/UrlHelper'

function filterMatchedCategories(categoryItems: WidgetCategoryLink[], products: PlpProduct[]) {
  const matchedLinks = new Set(
    products.flatMap((product) => (product.categoryUrlSegments || []).concat(product.subcategoryUrlSegments || []))
  )
  return categoryItems
    .map((category) => {
      const matchedSubcategories = (category.subcategories || []).filter((sub) => matchedLinks.has(sub.link))
      const isCategoryMatched = matchedLinks.has(category.link)
      if (isCategoryMatched || matchedSubcategories.length > 0) {
        return {
          ...category,
          subcategories: matchedSubcategories.length > 0 ? matchedSubcategories : undefined,
        }
      }
      return null
    })
    .filter(Boolean) as WidgetCategoryLink[]
}

function filterMatchedRegions(regions: WidgetRegionLink[], products: PlpProduct[]) {
  const matchedLinks = new Set(products.map((product) => product.primaryRegion))

  return regions
    .map((item) => {
      const matchedRegions = (item.regions || []).filter((sub) => matchedLinks.has(sub.link))
      const isCategoryMatched = matchedLinks.has(item.link)
      if (isCategoryMatched || matchedRegions.length > 0) {
        return {
          ...item,
          regions: matchedRegions.length > 0 ? matchedRegions : undefined,
        }
      }
      return null
    })
    .filter(Boolean) as WidgetRegionLink[]
}

const SearchTemplatePage: React.FC<{ searchResponse: SearchResponse }> = ({ searchResponse }) => {
  const products = searchResponse.products
  const [firstLoad, setFirstLoad] = useState(true)
  const [results, setResults] = useState<PlpProduct[] | undefined>(products)
  const [suggestions, setSuggestions] = useState<SearchResult[] | undefined>([])
  const router = useRouter()
  const defaultProperties = useContext(DefaultPropertiesContext)
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent
  const handlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const { lang } = router.query
  const baseUrl = getUrlPrefix(handlerKey)

  const getSearchQueryFromPath = (asPath: string, baseUrl: string): string => {
    try {
      const cleanPath = asPath.split('?')[0].split('#')[0]

      const normalizedPath = baseUrl ? cleanPath.replace(baseUrl, '') : cleanPath

      const segments = normalizedPath.split('/').filter(Boolean)

      const searchIndex = segments.indexOf('search')
      return searchIndex >= 0 ? decodeURIComponent(segments[searchIndex + 1] || '') : ''
    } catch (err) {
      console.error('Failed to parse search query from path:', err)
      return ''
    }
  }

  const searchQuery = getSearchQueryFromPath(router.asPath, baseUrl)

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false)
    } else {
      load(`${searchQuery}`)
    }
  }, [searchQuery])

  const load = async (query: string) => {
    const res = await (
      await fetch(`${baseUrl}/api/search`, {
        method: 'POST',
        body: JSON.stringify({ query, limit: 9999 }),
      })
    ).json()
    setResults(res.products)
    setSuggestions(res.suggestions)
  }

  const breadcrumbs = [{ text: 'Home', href: `/${lang}` }, { text: `Results for '${searchQuery}'` }]

  const getFilteredCategories = (): WidgetCategoryLink[] => {
    const items = defaultProperties?.searchProperties?.categories.map((a) => ({
      id: a.id,
      name: a.name,
      link: a.urlSegment,
      subcategories: a.subCategories?.map((s) => ({ id: s.id, name: s.name, link: s.urlSegment })),
    }))
    return filterMatchedCategories(items || [], results || [])
  }

  const getFilteredRegions = (): WidgetRegionLink[] | undefined => {
    const items = defaultProperties?.topbarConfiguration?.destinations?.[0]?.states.map((a) => ({
      id: (defaultProperties?.searchProperties?.regions?.find((b) => b.name === a.name)?.id || a.name) as any,
      name: a.name,
      link: a.url,
      regions: a?.regions.map((b) => ({
        id: (defaultProperties?.searchProperties?.regions?.find((c) => c.name === b.name)?.id || b.name) as any,
        name: b.name,
        link: b.url,
      })),
    }))
    return filterMatchedRegions(items || [], results || [])
  }

  const renderResults = () => {
    const filtered = results

    if (filtered?.length === 0 || !filtered) {
      return (
        <NoResultsWrapper>
          {searchResponse.loading ? (
            <Heading alignment="center" size="L">
              Loading...
            </Heading>
          ) : (
            <>
              <Heading alignment="center" size="M" bold={false}>
                Sorry, we couldn&apos;t find any results for {router.query?.part1}.
              </Heading>
              <Heading alignment="center" size="M" bold={false}>
                Please try a more generic search term, category or region.
              </Heading>
            </>
          )}
        </NoResultsWrapper>
      )
    }

    return (
      <ProductListContainer>
        <FiltersListingPreview
          entityType={'THINGS_TO_DO'}
          key={`${results[0].id}_${results.length}`}
          products={results}
          showTotal
          changeUrl
          alwaysShowAllFilterSideBar
          regions={getFilteredRegions()}
          categories={getFilteredCategories()}
        />
      </ProductListContainer>
    )
  }

  return (
    <>
      <Head>
        <meta name="pageid" content="search" />
        <title>
          {`Search ${formatSearchTitle(
            searchResponse.part1EntityType ?? null,
            searchResponse.part2EntityType ?? null,
            searchResponse.query?.part1 ?? '',
            searchResponse.query?.part2
          )}`}
        </title>
        <meta
          name="description"
          content="Search from over
        3,000 adventures to make your holiday or weekend extraordinary. Book now and pay later with Afterpay and Zip
        Pay accepted."
        />
        {getIndexTag(isBrgAgent, 'search', handlerKey || '')}
      </Head>
      <PageContent>
        <Container>
          <Breadcrumb items={breadcrumbs} dataTestId="breadcrumb-item" />
          <HeadContainer>
            <Heading size="XL" as="p" spacing={false}>
              Results for &apos;{searchQuery}&apos;
            </Heading>
          </HeadContainer>
          {suggestions && suggestions.length > 0 && (
            <Body size="M" as="div">
              <span style={{ marginRight: 12 }}>Did you mean </span>
              {suggestions.map((s, i) => (
                <span key={s.id} className="suggestion-link">
                  <Link href={s.url}>
                    <Body as="span">{s.name}</Body>
                  </Link>
                  {`${i === suggestions!.length - 1 ? '' : ','}`}
                </span>
              ))}{' '}
            </Body>
          )}
          <Spacing size="L" />
          {renderResults()}
        </Container>
        <Spacing size="L" />
      </PageContent>
    </>
  )
}

export default SearchTemplatePage
